import {createContext, useCallback, useContext, useState} from 'react';
import {useSelector} from 'react-redux';
import {partnerSongUploadLimitSelector} from '../../../../store/Slices/companySlice';

export const SONG_MAX_DURATION = 90;

interface ISongSessionContext {
  songSessionCount: number;
  songCountInitialized: boolean;
  songUploadLimit: number;
  incrementSongCount: () => void;
  decrementSongCount: () => void;
  initializeCount: (existingCount: number) => void;
  resetSession: () => void;
}

const SongSessionContext = createContext<ISongSessionContext | null>(null);

export const SongSessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const songUploadLimit = useSelector(partnerSongUploadLimitSelector);
  const [songSessionCount, setSongSessionCount] = useState(0);
  const [songCountInitialized, setSongCountInitialized] = useState(false);

  const incrementSongCount = useCallback(() => {
    setSongSessionCount(prev => prev + 1);
  }, []);

  const decrementSongCount = useCallback(() => {
    setSongSessionCount(prev => Math.max(0, prev - 1));
  }, []);

  const initializeCount = useCallback((existingCount: number) => {
    setSongSessionCount(existingCount + 1);
    setSongCountInitialized(true);
  }, []);

  const resetSession = useCallback(() => {
    setSongSessionCount(0);
    setSongCountInitialized(false);
  }, []);

  return (
    <SongSessionContext.Provider
      value={{
        songSessionCount,
        songCountInitialized,
        songUploadLimit: songUploadLimit,
        incrementSongCount,
        decrementSongCount,
        initializeCount,
        resetSession,
      }}>
      {children}
    </SongSessionContext.Provider>
  );
};

export const useSongSession = () => {
  const context = useContext(SongSessionContext);
  if (!context) {
    throw new Error('useSongSession must be used within a SongSessionProvider');
  }
  return context;
};
